import { ErrorFactory } from '../../../../../App/services/ErrorFactory';
import { HttpService } from '../../../../../App/services/http-service';
import { AuthenticationService } from '../../../../../Auth/mx-authentication-service';
import { OperationStatus, ProcessItem } from '../../../../Pandora/model/process-item';
import { DownloadHelpService } from '../../../../Pandora/services/mx-download-help-service';
import { AttachmentMetaInfo, IAttachmentProvider, SignedUploadUrl } from '../mx-attachment.contracts';

/**
 * @ngdoc provider
 * @name MxAttachmentDefaultProvider
 * @module mx.shell
 *
 * @description
 * Provides default implementation to work with attachments using internal SQL storage (see api/fileStorage)
 */

export class MxAttachmentDefaultProvider implements IAttachmentProvider {
	static $inject = ['mx.shell.AuthService', 'mx.SolutionBuilderAgent.Http', 'mx.shell.DownloadHelpService', 'mx.app.ErrorFactory'];
	private readonly apiController: string = 'api/fileStorage';
	public readonly isPublic = false;

	constructor(
		private readonly authService: AuthenticationService,
		private readonly http: HttpService,
		private readonly downloadHelpService: DownloadHelpService,
		private readonly errorFactory: ErrorFactory
	) {}

	public get providerId(): string {
		return '9766c0d4-50b6-ec11-de8c-381428440f29';
	}

	public async getFileUrl(attachmentInfo: AttachmentMetaInfo): Promise<string> {
		const url = `/${this.apiController}/${attachmentInfo.id}`;
		return attachmentInfo.inline ? `${url}/inline` : url;
	}

	public async uploadFile(attachmentInfo: AttachmentMetaInfo, process: ProcessItem) {
		const header = await this.getAuthHeader();
		const response = await this.http.post<SignedUploadUrl>(`${this.apiController}/getUploadUrl`, {
			uniqueFileId: attachmentInfo.id,
			storageId: attachmentInfo.storageId,
			name: attachmentInfo.name || 'dummy',
			thumbnail: attachmentInfo.thumbnail,
			size: attachmentInfo.size || 0,
			typeId: attachmentInfo.typeId,
			objectId: attachmentInfo.objectId,
			bindAttribute: attachmentInfo.bindAttribute,
		});

		const request = new Promise<void>((resolve: { (): void }, reject: { (reason?: Error): void }) => {
			const xhr = new XMLHttpRequest();
			process.onProcessCanceled(async () => {
				xhr.abort();
			});
			xhr.open('POST', response.Url, true);
			if (header) {
				xhr.setRequestHeader('Authorization', header.Authorization);
			}
			xhr.setRequestHeader('Content-Type', 'application/octet-stream');
			xhr.upload.onprogress = (event) => {
				process.progress = event.total > 0 ? Math.round((event.loaded * 100) / event.total) : 0;
			};

			xhr.upload.onerror = () => reject();

			xhr.onreadystatechange = () => {
				if (xhr.status === 500 && xhr.response) {
					reject(this.errorFactory.createApiError(xhr.response));
				}

				if (xhr.readyState === XMLHttpRequest.DONE && xhr.status === 204) {
					resolve();
				}
			};
			xhr.onloadstart = function () {
				process.operationStatus = OperationStatus.Started;
			};
			xhr.onabort = function () {
				process.operationStatus = OperationStatus.Canceled;
				reject();
			};
			xhr.onloadend = function (event) {
				if (event.total > 0 && event.total === event.loaded) {
					process.operationStatus = OperationStatus.Finished;
				}
			};
			xhr.send(attachmentInfo as File);
		});
		return await request;
	}

	public async downloadFile(attachmentInfo: AttachmentMetaInfo) {
		window.open(this.http.formatUrl(`${this.apiController}/${attachmentInfo.id}`));
		return true;
	}

	private async getAuthHeader() {
		const token = await this.authService.getToken();
		return { Authorization: `Bearer ${token}` };
	}
}
